import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Calendar, Users, MapPin, Clock, Settings, X } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './NotificationBell.css';

const NotificationBell = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Mock notifications - in production, fetch from API
  useEffect(() => {
    const mockNotifications = [
      {
        id: 1,
        type: 'rsvp',
        title: 'New RSVP',
        message: 'Sarah Johnson is attending your Morning Yoga Session',
        time: '2 minutes ago',
        read: false,
        icon: Users,
        eventId: 1,
      },
      {
        id: 2,
        type: 'reminder',
        title: 'Event Reminder',
        message: 'Basketball Pickup Game starts in 2 hours',
        time: '1 hour ago',
        read: false,
        icon: Clock,
        eventId: 2,
      },
      {
        id: 3,
        type: 'update',
        title: 'Event Update',
        message: 'Location changed for Tech Meetup',
        time: '3 hours ago',
        read: true,
        icon: MapPin,
        eventId: 3,
      },
      {
        id: 4,
        type: 'new_event',
        title: 'New Event Match',
        message: 'Coffee Chat matches your interests',
        time: '5 hours ago',
        read: true,
        icon: Calendar,
        eventId: 4,
      },
      {
        id: 5,
        type: 'cancelled',
        title: 'Event Cancelled',
        message: 'Photography Walk has been cancelled',
        time: '1 day ago',
        read: true,
        icon: X,
        eventId: 5,
      },
    ];

    setNotifications(mockNotifications);
    setUnreadCount(mockNotifications.filter((n) => !n.read).length);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = (notificationId) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.id);

    // Navigate based on notification type
    if (notification.eventId) {
      navigate(`/event/${notification.eventId}`);
    }
    setShowDropdown(false);
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'rsvp':
        return { bg: '#dcfce7', color: '#16a34a' };
      case 'reminder':
        return { bg: '#f0fdf4', color: '#15803d' };
      case 'update':
        return { bg: '#d1fae5', color: '#059669' };
      case 'new_event':
        return { bg: '#bbf7d0', color: '#047857' };
      case 'cancelled':
        return { bg: '#f3f4f6', color: '#6b7280' };
      default:
        return { bg: '#f8fafc', color: '#64748b' };
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        className="notification-bell-btn"
        onClick={() => setShowDropdown(!showDropdown)}
        aria-label="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            className="notification-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
          >
            <div className="notification-header">
              <h3>Notifications</h3>
              <div className="notification-actions">
                {unreadCount > 0 && (
                  <button onClick={markAllAsRead} className="mark-all-read-btn">
                    <Check size={16} />
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => {
                    navigate('/notifications/preferences');
                    setShowDropdown(false);
                  }}
                  className="settings-btn"
                >
                  <Settings size={16} />
                </button>
              </div>
            </div>

            <div className="notification-list">
              {notifications.length === 0 ? (
                <div className="no-notifications">
                  <Bell size={32} />
                  <p>No notifications yet</p>
                  <span>We'll notify you when something important happens</span>
                </div>
              ) : (
                notifications.slice(0, 5).map((notification) => {
                  const Icon = notification.icon;
                  const style = getNotificationStyle(notification.type);
                  return (
                    <div
                      key={notification.id}
                      className={`notification-item ${notification.read ? 'read' : 'unread'}`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div
                        className="notification-icon"
                        style={{ backgroundColor: style.bg, color: style.color }}
                      >
                        <Icon size={18} />
                      </div>
                      <div className="notification-content">
                        <h4>{notification.title}</h4>
                        <p>{notification.message}</p>
                        <span className="notification-time">{notification.time}</span>
                      </div>
                      {!notification.read && <div className="unread-dot" />}
                    </div>
                  );
                })
              )}
            </div>

            {notifications.length > 5 && (
              <div className="notification-footer">
                <button
                  onClick={() => {
                    navigate('/notifications');
                    setShowDropdown(false);
                  }}
                  className="view-all-btn"
                >
                  View all notifications
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
